import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme, skipBroadcast?: boolean) => void;
  resolvedTheme: 'light' | 'dark';
}

// BroadcastChannel for cross-tab theme synchronization
let themeChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  themeChannel = new BroadcastChannel('helios-theme-sync');
}

/**
 * Theme Store using Zustand
 *
 * Manages theme state with localStorage persistence.
 * Supports three modes: 'light', 'dark', 'system'
 *
 * Usage:
 * ```tsx
 * import { useThemeStore } from '@/stores/themeStore';
 *
 * function Component() {
 *   const { theme, setTheme, resolvedTheme } = useThemeStore();
 *   return <button onClick={() => setTheme('dark')}>Dark Mode</button>
 * }
 * ```
 */
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, _get) => ({
      theme: 'system', // Default to system theme
      resolvedTheme: (() => {
        if (typeof window === 'undefined') return 'dark';
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      })(),
      setTheme: (theme, skipBroadcast = false) => {
        // Calculate resolved theme (dark or light)
        let resolved: 'light' | 'dark' = 'dark';

        if (theme === 'system') {
          // Detect system preference
          resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        } else {
          resolved = theme;
        }

        // Single atomic set: subscribers get ONE notification per switch and can
        // never observe `theme` and `resolvedTheme` disagreeing mid-update.
        set({ theme, resolvedTheme: resolved });

        // Apply theme class to document root
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(resolved);

        // Broadcast theme change to other tabs (unless this is a broadcast-triggered update)
        if (themeChannel && !skipBroadcast) {
          themeChannel.postMessage({ theme });
        }
      },
    }),
    {
      name: 'helios-theme',
      // Initialize resolved theme after hydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setTheme(state.theme);
        }
      },
    }
  )
);

// Initialize theme on module load (prevent FOUC)
if (typeof window !== 'undefined') {
  const storedTheme = localStorage.getItem('helios-theme');
  if (storedTheme) {
    try {
      const parsed = JSON.parse(storedTheme);
      const theme = parsed.state?.theme || 'system';

      let resolved: 'light' | 'dark' = 'dark';
      if (theme === 'system') {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else {
        resolved = theme;
      }

      document.documentElement.classList.add(resolved);
    } catch {
      // Fallback to system theme
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      document.documentElement.classList.add(systemTheme);
    }
  } else {
    // Default to system theme
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
    document.documentElement.classList.add(systemTheme);
  }

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (_e) => {
    const store = useThemeStore.getState();
    if (store.theme === 'system') {
      store.setTheme('system'); // Re-apply system theme
    }
  });

  // Listen for theme changes from other tabs via BroadcastChannel
  if (themeChannel) {
    themeChannel.onmessage = (event: MessageEvent<{ theme: Theme }>) => {
      const store = useThemeStore.getState();
      if (store.theme !== event.data.theme) {
        // Update state without broadcasting again (skipBroadcast=true prevents infinite loop)
        const { theme } = event.data;
        store.setTheme(theme, true);
      }
    };
  }
}
